import { useQuery } from '@tanstack/react-query';
import {
  ROTULO_ESTADO_SLA,
  ROTULO_ETAPA,
  ROTULO_SCORE,
  descreverEsperaSemContato,
  formatarReais,
  type EstadoSla,
  type EtapaFunil,
  type Score,
} from '@reducee/contracts';

import { chamarApi } from '../api/cliente.ts';
import { Medalha } from './Superficie.tsx';

export type ItemDaFila = {
  oportunidadeId: string;
  produto: string;
  score: Score;
  etapa: EtapaFunil;
  valorMensalCentavos: number;
  estadoSla: EstadoSla | null;
  minutosSemPrimeiroContato: number;
  proximoPasso: string | null;
  contato: { id: string; nome: string; telefone: string | null; whatsapp: string | null };
  responsavel: { id: string; nome: string };
};

/** Cor do tempo sem primeiro contato, pelo estado do SLA. */
const COR_DO_SLA: Record<EstadoSla, string> = {
  ESTOURADO: 'text-[#b42318] font-extrabold',
  EM_RISCO: 'text-[#a86d00] font-extrabold',
  NO_PRAZO: 'text-[#257a4b] font-extrabold',
  ATENDIDO: 'text-[#257a4b] font-extrabold',
  // Sem destaque: o resgatado não cobra prazo nenhum da Comercial.
  RESGATADO: 'text-texto-suave',
};

/**
 * Quatro colunas fixas davam 60px ao SLA num celular, e "7983 min sem contato"
 * quebrava numa palavra por linha. Abaixo de `md` o nome e o próximo passo
 * ocupam a linha inteira, e valor e SLA dividem a de baixo.
 */
const CLASSE_DA_LINHA =
  'grid grid-cols-2 items-center gap-x-2.5 gap-y-1 border-b border-[#e8ebf1] py-2.5 last:border-0 md:grid-cols-[1.5fr_.8fr_.8fr_1fr] md:gap-y-0';

/**
 * Fila de prioridade, no formato de linha do protótipo.
 *
 * Cada linha diz, da esquerda para a direita, o que decide o atendimento: quem
 * é, quanto vale, há quanto tempo espera e o que foi combinado. A ordem vem da
 * API, que aplica os critérios do domínio — a tela não reordena, porque duas
 * usuárias precisam ver a mesma prioridade.
 */
export function FilaDeAtendimento({ limite = 7 }: { limite?: number }): React.JSX.Element {
  const fila = useQuery({
    queryKey: ['atender-agora'],
    queryFn: () => chamarApi<ItemDaFila[]>('/atender-agora'),
  });

  if (fila.isLoading) return <div className="py-4.5 text-xs text-texto-suave">Carregando…</div>;

  const itens = (fila.data ?? []).slice(0, limite);

  if (itens.length === 0) {
    return (
      <div className="py-4.5 text-xs text-texto-suave">Nenhum lead aguardando priorização.</div>
    );
  }

  return (
    <div>
      {itens.map((item) => (
        <div
          key={item.oportunidadeId}
          data-teste="item-fila"
          className={CLASSE_DA_LINHA}
        >
          <div className="col-span-2 min-w-0 md:col-span-1">
            <b className="block truncate">{item.contato.nome}</b>
            <div className="truncate text-[11px] text-texto-suave">
              <Medalha score={item.score}>{ROTULO_SCORE[item.score]}</Medalha> ·{' '}
              {ROTULO_ETAPA[item.etapa]} · {item.responsavel.nome}
            </div>
          </div>

          <div className="min-w-0">{formatarReais(item.valorMensalCentavos)}</div>

          <div className={`min-w-0 ${item.estadoSla ? COR_DO_SLA[item.estadoSla] : ''}`}>
            {/* O resgatado tem zero minuto sem contato porque o prazo nunca
                correu, e não porque alguém o atendeu: dizer "Contatado" aqui
                seria afirmar um atendimento que não houve. */}
            {item.estadoSla === 'RESGATADO'
              ? ROTULO_ESTADO_SLA.RESGATADO
              : item.minutosSemPrimeiroContato > 0
                ? descreverEsperaSemContato(item.minutosSemPrimeiroContato)
                : 'Contatado'}
          </div>

          <div className="col-span-2 min-w-0 truncate md:col-span-1">
            {item.proximoPasso ? (
              item.proximoPasso
            ) : (
              <span className="font-extrabold text-[#b42318]">Sem próximo passo</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
